import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Alert,
  Dialog,
  DialogContent
} from '@mui/material';
import EventTableV2 from './aggregator/EventTableV2';
import EventFilters from './aggregator/EventFilters';
import PollIndicator from './aggregator/PollIndicator';
import EventTicker from './aggregator/EventTicker';
import LocationMapModal from '../components/ui/LocationMapModal';
import StatementTemplateSelector from './statements/StatementTemplateSelector';
import WitnessStatementForm from './statements/WitnessStatementForm';
import { useHybridEventSource } from './aggregator/useHybridEventSource';
import { useEventPagination } from './aggregator/useEventPagination';
import { useMapModal } from './aggregator/useMapModal';
import { POLLING_CONFIG, DEFAULT_VISIBLE_EVENT_TYPES } from '../config/appConfig';

const POLL_INTERVAL = POLLING_CONFIG.EVENT_AGGREGATOR_INTERVAL;

/**
 * EventAggregatorApp - Displays aggregated events for operators
 *
 * Features:
 * - Real-time updates via WebSocket (with HTTP polling fallback)
 * - Auto-polling every 5 seconds (when WebSocket disabled)
 * - Infinite scroll pagination
 * - Event type filtering
 * - Click location to view on map
 * - Click assignment to view all matches in that assignment
 */
export default function EventAggregatorApp() {
  // Event data state
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latestEvent, setLatestEvent] = useState([]);
  const tickerTriggerRef = useRef(0);

  // Filter state - using object format for consistency with History settings
  const [eventTypeFilters, setEventTypeFilters] = useState(DEFAULT_VISIBLE_EVENT_TYPES);

  // Statement creation state
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Pagination hook
  const {
    hasMore,
    loadingMore,
    hasPaginated,
    pageSize,
    currentPage,
    setPageSize,
    loadMoreEvents,
    goToNextPage,
    goToPreviousPage,
    setNextToken,
    setHasMore
  } = useEventPagination({
    events,
    setEvents,
    setError
  });

  // Callback for immediate ticker updates
  const handleNewEvent = useCallback((event) => {
    console.log('[EventAggregator] Immediate new event for ticker:', event.eventType, event.eventId?.substring(0, 15));
    tickerTriggerRef.current += 1;
    setLatestEvent([{ ...event, _tickerTrigger: tickerTriggerRef.current }]); // Add unique trigger
  }, []);

  // Hybrid event source (WebSocket with polling fallback)
  // Automatically switches based on feature flag configuration
  const { lastPoll, hasNewEvents, newEventCount, isPulsing, _transport, _wsStatus, _wsReconnect } = useHybridEventSource({
    events,
    setEvents,
    setError,
    setLoading,
    pollInterval: POLL_INTERVAL,
    pageSize,
    enabled: true, // Always enabled (hook handles transport selection)
    setNextToken,
    setHasMore,
    hasPaginated,
    onNewEvent: handleNewEvent // Immediate callback for ticker
  });

  // Debug: Log transport mode (remove in production)
  useEffect(() => {
    if (_transport) {
      console.log(`[EventAggregator] Transport: ${_transport}, WebSocket status: ${_wsStatus}`);
    }
  }, [_transport, _wsStatus]);

  // Map modal hook - pass all events for filtering
  const {
    mapModalOpen,
    mapModalData,
    openPersonMap,
    openGroupMap,
    openAssignmentMap,
    closeMap,
    refreshMapData
  } = useMapModal(events);

  // Handle location icon click
  const handleLocationClick = async (event) => {
    if (event.eventType === 'assignment') {
      await openAssignmentMap(event);
    } else if (event.eventType === 'group_match') {
      await openGroupMap(event);
    } else if (event.location) {
      await openPersonMap(event);
    }
  };

  // Scroll to top when new events indicator is clicked
  // Also attempt WebSocket reconnect (works even if fallen back to polling)
  const handleNewEventsClick = () => {
    const scrollableDiv = document.getElementById('aggregatorScrollableDiv');
    if (scrollableDiv) {
      scrollableDiv.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Always attempt WebSocket reconnect if reconnect function exists
    // This works even when in polling fallback mode - it will try to restore WebSocket
    if (_wsReconnect) {
      console.log('[EventAggregator] Attempting WebSocket reconnect (current transport:', _transport, ')');
      _wsReconnect();
    }
  };

  // Refresh map when new events arrive
  useEffect(() => {
    if (hasNewEvents && mapModalOpen) {
      refreshMapData();
    }
  }, [hasNewEvents, mapModalOpen, refreshMapData]);

  // Handle statement creation from event
  const handleCreateStatement = (event) => {
    setSelectedEvent(event);
    setShowTemplateSelector(true);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setShowTemplateSelector(false);
  };

  const handleStatementSaved = () => {
    setSelectedTemplate(null);
    setSelectedEvent(null);
  };

  const handleCancelStatement = () => {
    setShowTemplateSelector(false);
    setSelectedTemplate(null);
    setSelectedEvent(null);
  };

  // Filter events by selected types (object format: {eventType: true/false})
  const filteredEvents = events.filter(e => eventTypeFilters[e.eventType] !== false);

  if (error) {
    return (
      <Paper sx={{ p: 3, height: '100%' }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <>
      <Paper sx={{ p: 1, display: 'flex', flexDirection: 'column',maxWidth: '95%', maxHeight: '96%', overflow: 'hidden' }}>
        {/* Event Ticker */}
        <EventTicker newEvents={latestEvent} />

        {/* Event table */}
        <EventTableV2
          events={filteredEvents}
          loading={loading}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={loadMoreEvents}
          onLocationClick={handleLocationClick}
          onAssignmentClick={openAssignmentMap}
          onCreateStatement={handleCreateStatement}
        />

        {/* Filters footer */}
        <EventFilters
          selectedFilters={eventTypeFilters}
          onFilterChange={setEventTypeFilters}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          currentPage={currentPage}
          totalEvents={events.length}
          onPreviousPage={goToPreviousPage}
          onNextPage={goToNextPage}
          hasMore={hasMore}
        />

        {/* Map modal */}
        {mapModalData && (
          <LocationMapModal
            open={mapModalOpen}
            onClose={closeMap}
            events={mapModalData.events}
            personName={mapModalData.personName}
            personImage={mapModalData.personImage}
          />
        )}
      </Paper>

      {/* Floating poll indicator (chatbot-style, always visible) */}
      <Box sx={{
        position: 'fixed',
        top: 80,
        right: 16,
        zIndex: 1000,
        pointerEvents: 'none',
        '& > *': {
          pointerEvents: 'auto'
        }
      }}>
        <PollIndicator
          loading={loading}
          lastPoll={lastPoll}
          hasNewEvents={hasNewEvents}
          newEventCount={newEventCount}
          isPulsing={isPulsing}
          onNewEventsClick={handleNewEventsClick}
          transport={_transport}
        />
      </Box>

      {/* Statement Template Selector Dialog */}
      <Dialog
        open={showTemplateSelector}
        onClose={handleCancelStatement}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <StatementTemplateSelector
            onSelect={handleTemplateSelect}
            onCancel={handleCancelStatement}
          />
        </DialogContent>
      </Dialog>

      {/* Witness Statement Form Dialog */}
      <Dialog
        open={selectedTemplate !== null}
        onClose={handleCancelStatement}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent>
          {selectedTemplate && selectedEvent && (
            <WitnessStatementForm
              template={selectedTemplate}
              eventData={selectedEvent}
              onSave={handleStatementSaved}
              onCancel={handleCancelStatement}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
